import { isLayoutType, makeField } from '../../shared/fieldTypes';

// Snapshots (build plan §5.5): the builder's plain copy of everything
// `saveForm` writes. `saved` mirrors the server; `working` is what the
// admin is editing. Both have exactly SETTING_KEYS plus `fields`.

export const FIELD_KEYS = [
  'id',
  'type',
  'label',
  'help',
  'placeholder',
  'required',
  'map',
  'acct',
  'config',
];

export const SETTING_KEYS = [
  'name',
  'description',
  'account_id',
  'layout',
  'show_progress',
  'confirmation_email',
  'save_and_resume',
  'notify_on_submit',
  'notify_recipients',
];

const isMissing = (value) => value === undefined || value === null;
const copy = (value) => (Array.isArray(value) ? value.slice() : value);

// Config keys a type keeps only when they're set (they have no default):
// an agreement's checkbox text, a choice's "Other" option (T22) and a
// description's boxed card (w9-plan §2.3).
export const OPTIONAL_CONFIG_KEYS = {
  checkbox: ['agree_label'],
  dropdown: ['allow_other'],
  radio: ['allow_other'],
  description: ['boxed'],
};

const LAYOUT_FORCED = {
  required: false,
  map: null,
  acct: false,
  placeholder: '',
};

// A field in the §2.3 shape: the 9 keys in order, config keys in the type's
// default order (then any OPTIONAL_CONFIG_KEYS that are set), missing
// values from the type default, unknown keys dropped (e.g. `chosen` from
// react-sortablejs). Layout types get the forced values. An unknown type
// comes back untouched.
export const canonicalField = (raw) => {
  const base =
    raw && typeof raw === 'object' ? makeField(raw.type, raw.id) : null;
  if (!base) return raw;
  const out = {};
  FIELD_KEYS.forEach((key) => {
    out[key] = isMissing(raw[key]) ? base[key] : raw[key];
  });
  const source = raw.config && typeof raw.config === 'object' ? raw.config : {};
  const config = {};
  Object.keys(base.config).forEach((key) => {
    config[key] = isMissing(source[key]) ? base.config[key] : copy(source[key]);
  });
  (OPTIONAL_CONFIG_KEYS[raw.type] || []).forEach((key) => {
    if (!isMissing(source[key])) config[key] = copy(source[key]);
  });
  out.config = config;
  return isLayoutType(raw.type) ? { ...out, ...LAYOUT_FORCED } : out;
};

export const recipientOf = (r) => ({
  email: r.email,
  name: r.name || null,
  user_id: isMissing(r.user_id) ? null : r.user_id,
});

const definitionOfForm = (form) => {
  const version = form && form.working_version;
  let definition = version ? version.definition : null;
  if (typeof definition === 'string') {
    try {
      definition = JSON.parse(definition);
    } catch (e) {
      definition = null;
    }
  }
  return definition && Array.isArray(definition.fields)
    ? definition
    : { schema: 1, fields: [] };
};

// BuilderForm (FormDetailFields) → Snapshot.
export const snapshotOf = (form) => ({
  name: form.name || '',
  description: form.description || '',
  account_id: form.account ? form.account.id : null,
  layout: form.layout === 'wizard' ? 'wizard' : 'scroll',
  show_progress: !!form.show_progress,
  confirmation_email: !!form.confirmation_email,
  save_and_resume: !!form.save_and_resume,
  notify_on_submit: !!form.notify_on_submit,
  notify_recipients: (form.notify_recipients || []).map(recipientOf),
  fields: definitionOfForm(form).fields.map(canonicalField),
});

// The definition document for a snapshot (what Preview renders and what
// saveForm stores).
export const definitionOf = (snapshot) => ({
  schema: 1,
  fields: snapshot.fields.map(canonicalField),
});

// Snapshot → the full SaveFormInput (plan §3.2). Every save sends every key.
export const snapshotToSaveInput = (working, formId, revision) => ({
  id: formId,
  revision,
  name: working.name,
  description: working.description,
  account_id: working.account_id,
  layout: working.layout,
  show_progress: working.show_progress,
  confirmation_email: working.confirmation_email,
  save_and_resume: working.save_and_resume,
  notify_on_submit: working.notify_on_submit,
  notify_recipients: working.notify_recipients.map(recipientOf),
  definition: definitionOf(working),
});
